import React, { FC, useState, useCallback, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from "react-svg"
import { useDropzone } from 'react-dropzone'
import Encoding from 'encoding-japanese'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Helmet from 'react-helmet'
import { RootState } from '../../../../../states/containers/store'
import { fetchCsvData, uploadCsvData, downloadCsvData } from '../../../../../states/containers/setting/importCsvSlice'
import Modal from '../../../../components/common/Modal'
import iconv = require('iconv-lite')
import VideoGuide from '../../../../components/common/VideoGuide'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { partition } from 'lodash'

type LangValue = 'ja' | 'en'

const Index: FC = () => {
  const alert = useAlert()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    importCsv,
  } = useSelector((state: RootState) => (state))

  const [step, setStep] = useState(1)
  const [code, setCode] = useState("Shift-JIS")
  const [selectedFileName, setSelectedFileName] = useState(t("setting.import.csv.step2.input.file_not_select"))
  const [fileData, setFileData] = useState('')
  const [isDescriptionDisplayModal, setDescriptionDisplayModal] = useState(false)
  const [isDisplayModal, setDisplayModal] = useState(false)
  const [isUpload, setUpload] = useState(false)
  const [rowerrors, setRowerrors] = useState<{ line: number, errors: string[] }[]>([])
  const [globalErrors, setGlobalErrors] = useState<string[]>([])

  const [isSendInvitation, setIsSendInvitation] = useState<boolean>(true)

  const onDrop = useCallback((acceptedFiles) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target === null || e.target.result === null) return
      setSelectedFileName(acceptedFiles[0].name)
      const buf = new Uint8Array(e.target.result as ArrayBuffer)
      const codes = Encoding.detect(new Uint8Array(buf))
      if (codes === 'UNICODE' || codes === 'SJIS') {
        setFileData(iconv.decode(Buffer.from(buf), "windows-31j"))
        return
      }

      setFileData(new TextDecoder("utf-8").decode(buf))
    }
    reader.onerror = () => alert.error(t("setting.import.csv.error.file_load"))
    reader.readAsArrayBuffer(acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const buildSVG = (iconFileName: string, color?: string) => {
    const filePath = `/static/images/import/csv/${iconFileName}.svg`
    return (
      <ReactSVG
        src={filePath}
        beforeInjection={(svg) => {
          if (color !== undefined) svg.setAttribute("stroke", color)
        }}
        wrapper="span"
      />
    )
  }

  const handleCancel = () => location.reload()
  const handleDownloadCsv = () => dispatch(downloadCsvData(code))

  const handleGetCsv = () => {
    setUpload(true)
    dispatch(fetchCsvData(fileData,
      () => {
        setUpload(false)
        setStep(step + 1)
      },
      () => setUpload(false),
      (errors: { line: number, errors: string[] }[]) => {
        const tmp = errors.sort((a, b) => a.line - b.line)
        const tmp_partition = partition(tmp, (v) => v.line === 0)

        setGlobalErrors(tmp_partition[0].flatMap((v) => v.errors))
        setRowerrors(tmp_partition[1])
        setUpload(false)
      }))
  }

  const handleUploadCsv = () => {
    setDisplayModal(false)
    setUpload(true)
    dispatch(uploadCsvData({
      file: fileData,
      send_mail: isSendInvitation,
    },
    () => setStep(step + 1),
    () => setUpload(false),
    () => setUpload(false)))
  }

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [])

  const renderStep1 = () => (
    <>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-detail step1-image-area">
            <ol className="step1">
              <li>
                <object type="image/svg+xml" title="" data="/static/images/import/csv/img-step1.svg">a</object>
                <br />
                <br />
                <p className="text">
                  {t("setting.import.csv.step1.description1_1")}
                  <br />
                  {t("setting.import.csv.step1.description1_2")}
                </p>
              </li>
              <li className="second">
                <object type="image/svg+xml" title="" data="/static/images/import/csv/img-step2.svg">a</object>
                <br />
                <br />
                <p className="text">{t("setting.import.csv.step1.description2")}</p>
              </li>
              <li className="third">
                <object type="image/svg+xml" title="" data="/static/images/import/csv/img-step3.svg">a</object>
                <br />
                <br />
                <p className="text">{t("setting.import.csv.step1.description3")}</p>
              </li>
            </ol>
          </div>
          <div style={{ width: "100%", marginTop: "80px", marginBottom: "80px" }}>
            <div className="text-center">
              <button
                type="button"
                className="start-btn"
                onClick={() => setStep(step + 1)}
              >
                {t("setting.import.csv.step1.button")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  )

  const updateCode = (event: React.ChangeEvent<HTMLSelectElement>) => setCode(event.target.value)
  const renderStep2 = () => (
    <>
      <section className="page_contents content-area">
        <div className="content-block">
          <nav>
            <ol className="stepBar">
              <li>
                <span className="active">1</span>
                <br />
                <p className="active">
                  {t("setting.import.csv.step1.description1_1")}
                  <br />
                  {t("setting.import.csv.step1.description1_2")}
                </p>
              </li>
              <li>
                <span>2</span>
                <br />
                <p>{t("setting.import.csv.step1.description2")}</p>
              </li>
              <li>
                <span>3</span>
                <br />
                <p>{t("setting.import.csv.step1.description3")}</p>
              </li>
            </ol>
          </nav>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block block-padding">
          <div className="step-title">{t("setting.import.csv.step2.title")}</div>
          <div className="step-body-start">{t("setting.import.csv.step2.description")}</div>
          <div className="step-body-start">
            <div className="encode">
              <span className="title">{t("setting.import.csv.step2.encode.title")}</span>
              <span className="description">{t("setting.import.csv.step2.encode.description")}</span>
            </div>
            <div className="code-select-area select">
              <select name="code" onChange={(e) => { updateCode(e) }}>
                <option value="Shift-JIS">Shift-JIS</option>
                <option value="UTF-8">UTF-8</option>
              </select>
            </div>
          </div>
          <div className="step-body-start" style={{ paddingLeft: "16px", color: "#4472BA" }}>
            <button type="button" className="link-style-btn" onClick={handleDownloadCsv}>
              {buildSVG('icon-download', '#4472BA')}
              <span className="text">{t("setting.import.csv.step2.download")}</span>
            </button>
          </div>
          <div className="step-body-start">
            <button type="button" className="link-style-btn" onClick={() => setDescriptionDisplayModal(true)}>
              {buildSVG('icon-help')}
              <span className="text font-black">{t("setting.import.csv.step2.precautions.title")}</span>
            </button>
          </div>
          <div className="step-body-start precautions">
            <div className="title">{t("setting.import.csv.step2.precautions.description.precautions.title")}</div>
            <div className="description">
              <p>
                ※
                {t("setting.import.csv.step2.precautions.description.precautions.description1")}
                <br />
                ※
                {t("setting.import.csv.step2.precautions.description.precautions.description2")}
                <br />
                ※
                {t("setting.import.csv.step2.precautions.description.precautions.description3")}
                <br />
              </p>
            </div>
          </div>

          <div {...getRootProps()} className="upload-area">
            <input {...getInputProps()} disabled={isUpload === true} />
            <div className="input-area">
              <div className="text-area">
                <p className="text">
                  {t("setting.import.csv.step2.input.description1")}
                  <br />
                  {t("setting.import.csv.step2.input.description2")}
                </p>
                <div className="file-area">
                  <button className="upload" type="button" disabled={isUpload === true}>{t("setting.import.csv.step2.input.button")}</button>
                  <span className="file-name">{selectedFileName}</span>
                </div>
                <div className="error-area">
                  { globalErrors.length > 0 && <div className="error-title">ファイル全体エラー</div> }
                  {
                    globalErrors.map((v) => (
                      <div className="row-error" key={v}>
                        {t(v)}
                        <br />
                      </div>
                    ))
                  }
                </div>
                <div className="error-area">
                  { rowerrors.length > 0 && <div className="error-title">行別エラー</div> }
                  {
                    rowerrors.map((v) => (
                      <div className="row-error" key={v.line}>
                        {t('company_csv_import.validate.line').replace('%{ num }', String(v.line))}
                        <br />
                        {
                          v.errors.map((error) => (
                            <div key={error}>
                              {t(error)}
                              <br />
                            </div>
                          ))
                        }
                      </div>
                    ))
                  }
                </div>
              </div>
              <div className="image-area">
                {buildSVG('img-csv-drag')}
              </div>
            </div>
          </div>
          <div className="step-body-start">
            <button
              type="button"
              className="cancel"
              onClick={handleCancel}
              disabled={isUpload === true}
            >
              {t("setting.import.csv.step2.button.cancel")}
            </button>
            <button
              type="button"
              className="upload margin"
              onClick={handleGetCsv}
              style={{ opacity: fileData.length === 0 ? 0.3 : 1 }}
              disabled={fileData.length === 0 || isUpload === true}
            >
              {
                isUpload === true ? (
                  <div className="loading_mini" />
                ) : (
                  <>{t("setting.import.csv.step2.button.upload")}</>
                )
              }
            </button>
          </div>
        </div>
      </section>
      <Modal
        title={t("setting.import.csv.step2.precautions.title")}
        show={isDescriptionDisplayModal}
        closeable
        wrapperClass=""
        closeCallback={() => setDescriptionDisplayModal(false)}
      >
        <>
          <div>
            <div>
              <div style={{ fontSize: "16px", color: "#4472BA" }}>
                {t("setting.import.csv.step2.precautions.description.input.title")}
              </div>
              {/* eslint-disable react/no-danger */}
              <div
                style={{ paddingTop: "16px", lineHeight: "24px" }}
                dangerouslySetInnerHTML={{ __html: t("setting.import.csv.step2.precautions.description.input.description") }}
              />
              {/* eslint-enable react/no-danger */}
            </div>
            <div style={{ paddingTop: "24px" }}>
              <div style={{ fontSize: "16px", color: "#4472BA" }}>
                {t("setting.import.csv.step2.precautions.description.occupation.title")}
              </div>
              <div style={{ paddingTop: "16px", lineHeight: "24px" }}>
                <span style={{ color: "#4472BA" }}>{t("setting.import.csv.step2.precautions.description.occupation.description1")}</span>
                <br />
                {t("setting.import.csv.step2.precautions.description.occupation.description2")}
              </div>
            </div>
            <div style={{ paddingTop: "24px" }}>
              <div style={{ fontSize: "16px", color: "#4472BA" }}>{t("setting.import.csv.step2.precautions.description.check.title")}</div>
              <div style={{ paddingTop: "16px", lineHeight: "24px" }}>
                {t("setting.import.csv.step2.precautions.description.check.description1")}
                <br />
                ・
                {t("setting.import.csv.step2.precautions.description.check.description2")}
                <br />
                ・
                {t("setting.import.csv.step2.precautions.description.check.description3")}
              </div>
            </div>

            <div style={{ paddingTop: "24px" }}>
              <div style={{ fontSize: "16px", color: "#4472BA" }}>{t("setting.import.csv.step2.precautions.description.precautions.title")}</div>
              <div style={{ paddingTop: "16px", lineHeight: "24px" }}>
                ・
                {t("setting.import.csv.step2.precautions.description.precautions.description1")}
                <br />
                ・
                {t("setting.import.csv.step2.precautions.description.precautions.description2")}
                <br />
                ・
                {t("setting.import.csv.step2.precautions.description.precautions.description3")}
                <br />
              </div>
            </div>

          </div>
        </>
      </Modal>
    </>
  )

  const getText = (word: string) => {
    switch (word) {
      case "create":
        return t("setting.import.csv.step3.description.add")
      case "change":
        return t("setting.import.csv.step3.description.change")
      case "delete":
        return t("setting.import.csv.step3.description.del")
      case "none":
        return t("setting.import.csv.step3.description.none")
      default:
        return t("setting.import.csv.step3.description.none")
    }
  }

  const renderStep3 = () => (
    <>
      <section className="page_contents content-area">
        <div className="content-block">
          <nav>
            <ol className="stepBar">
              <li>
                <span className="check">
                  <FontAwesomeIcon icon={['fas', 'check']} />
                </span>
                <br />
                <p>
                  {t("setting.import.csv.step1.description1_1")}
                  <br />
                  {t("setting.import.csv.step1.description1_2")}
                </p>
              </li>
              <li>
                <span className="active">2</span>
                <br />
                <p className="active">{t("setting.import.csv.step1.description2")}</p>
              </li>
              <li>
                <span>3</span>
                <br />
                <p>{t("setting.import.csv.step1.description3")}</p>
              </li>
            </ol>
          </nav>
        </div>
      </section>
      <section className="content-area">
        <div className="content-block">
          <div className="step-title">{t("setting.import.csv.step3.title")}</div>
          <div className="step-body-start">
            <p style={{ fontSize: "14px", color: "black" }}>
              {t("setting.import.csv.step3.description.total")}
              {importCsv.csvData.length}
              {t("setting.import.csv.step3.description.value")}
              (
              {t("setting.import.csv.step3.description.add")}
              :&nbsp;
              {importCsv.csvData.filter((data) => data.change === "create").length}
              {t("setting.import.csv.step3.description.value")}
              :&nbsp;
              /
              :&nbsp;
              {t("setting.import.csv.step3.description.del")}
              :&nbsp;
              {importCsv.csvData.filter((data) => data.change === "delete").length}
              {t("setting.import.csv.step3.description.value")}
              :&nbsp;
              /
              :&nbsp;
              {t("setting.import.csv.step3.description.change")}
              :&nbsp;
              {importCsv.csvData.filter((data) => data.change === "change").length}
              {t("setting.import.csv.step3.description.value")}
              :&nbsp;
              /
              :&nbsp;
              {t("setting.import.csv.step3.description.none")}
              :&nbsp;
              {importCsv.csvData.filter((data) => data.change === "none").length}
              {t("setting.import.csv.step3.description.value")}
              )
              <br />
              {t("setting.import.csv.step3.description.text1")}
              <br />
              {t("setting.import.csv.step3.description.text2")}
            </p>
          </div>
          <div className="step-body-start">
            <table className="detail-table">
              <thead>
                <tr>
                  <td><div>{t("setting.import.csv.step3.table.title.change")}</div></td>
                  <td>
                    <div>
                      {t("setting.import.csv.step3.table.title.name")}
                      <br />
                      (社員コード)
                    </div>
                  </td>
                  <td><div>{t("setting.import.csv.step3.table.title.management_team")}</div></td>
                  <td><div>{t("setting.import.csv.step3.table.title.member_team")}</div></td>
                  <td><div>{t("setting.import.csv.step3.table.title.occupation")}</div></td>
                  <td><div>{t("setting.import.csv.step3.table.title.contract")}</div></td>
                  <td><div>{t("setting.import.csv.step3.table.title.place")}</div></td>
                  <td><div>{t("setting.import.csv.step3.table.title.email")}</div></td>
                </tr>
              </thead>
              <tbody>
                {
                  importCsv.csvData.map((data) => (
                    <tr>
                      <td>
                        <div className="table-data">
                          <div className={`radius ${data.change}`}>{getText(data.change)}</div>
                        </div>
                      </td>
                      <td>
                        <div className={`table-data ${data.change}`}>
                          <ruby>
                            {data.name}
                            <rt>{data.kana}</rt>
                          </ruby>
                          {
                            data.code && (
                              <div>
                                {`(${data.code})`}
                              </div>
                            )
                          }
                        </div>
                      </td>
                      <td>
                        <div className={`table-data ${data.change}`}>
                          {
                            data.manager_team.length === 0 ? (
                              <span>－</span>
                            ) : (
                              data.manager_team.join("・")
                            )
                          }
                        </div>
                      </td>
                      <td>
                        <div className={`table-data ${data.change}`}>
                          {
                            data.member_team.length === 0 ? (
                              <span>－</span>
                            ) : (
                              data.member_team.join("・")
                            )
                          }
                        </div>
                      </td>
                      <td><div className={`table-data ${data.change}`}>{t(data.occupation)}</div></td>
                      <td><div className={`table-data ${data.change}`}>{data.contract}</div></td>
                      <td><div className={`table-data ${data.change}`}>{data.place}</div></td>
                      <td><div className={`table-data ${data.change}`}>{data.email}</div></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
          <div className="step-body-start">
            <button
              type="button"
              className="cancel"
              onClick={handleCancel}
              disabled={isUpload === true}
            >
              {t("setting.import.csv.step3.button.cancel")}
            </button>
            <button
              type="button"
              className="upload margin"
              onClick={() => setDisplayModal(true)}
              disabled={fileData.length === 0 || isUpload === true}
            >
              {t("setting.import.csv.step3.button.upload")}
            </button>
          </div>
        </div>
      </section>
      <Modal
        iconPath="/static/images/import/csv/icon-triangle.svg"
        title={t("setting.import.csv.step3.modal.title")}
        show={isDisplayModal}
        closeable
        wrapperClass=""
        closeCallback={() => setDisplayModal(false)}
      >
        <>
          <div>
            <p>
              {t("setting.import.csv.step3.modal.description1")}
              <br />
              {t("setting.import.csv.step3.modal.description2")}
            </p>
            <div
              onClick={() => setIsSendInvitation(!isSendInvitation)}
              role="none"
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FontAwesomeIcon
                icon={['far', `${isSendInvitation ? 'check-square' : 'square'}` as IconName]}
                className={`icon ${isSendInvitation ? "checked" : "no_check"}`}
                fixedWidth
                style={{
                  fontSize: "18px",
                  color: isSendInvitation ? "#008800" : "#666666",
                }}
              />
              新規登録ユーザーに招待メールを送信する
            </div>
          </div>
        </>
        <>
          <button
            type="button"
            className="cancel modal_cancel"
            onClick={() => setDisplayModal(false)}
            disabled={isUpload === true}
          >
            {t("setting.import.csv.step3.modal.button.cancel")}
          </button>
          <button
            type="button"
            onClick={handleUploadCsv}
            disabled={fileData.length === 0 || isUpload === true}
          >
            {
              isUpload === true ? (
                <div className="loading_mini" />
              ) : (
                <>{t("setting.import.csv.step3.modal.button.ok")}</>
              )
            }
          </button>
        </>
      </Modal>
    </>
  )

  const renderStep4 = () => (
    <>
      <section className="page_contents content-area">
        <div className="content-block">
          <nav>
            <ol className="stepBar">
              <li>
                <span className="check">
                  <FontAwesomeIcon icon={['fas', 'check']} />
                </span>
                <br />
                <p>
                  {t("setting.import.csv.step1.description1_1")}
                  <br />
                  {t("setting.import.csv.step1.description1_2")}
                </p>
              </li>
              <li>
                <span className="check">
                  <FontAwesomeIcon icon={['fas', 'check']} />
                </span>
                <br />
                <p>{t("setting.import.csv.step1.description2")}</p>
              </li>
              <li>
                <span className="active">3</span>
                <br />
                <p className="active">{t("setting.import.csv.step1.description3")}</p>
              </li>
            </ol>
          </nav>
        </div>
      </section>
      <section className="content-area">
        <div className="content-block" style={{ paddingLeft: "8px" }}>
          <div className="step-title">{t("setting.import.csv.step4.title")}</div>
          {/* eslint-disable react/no-danger */}
          <div
            className="step-body-start"
            dangerouslySetInnerHTML={{ __html: t("setting.import.csv.step4.description") }}
          />
          {/* eslint-enable react/no-danger */}
          <div className="step-body-start">
            <button
              type="button"
              className="upload"
              onClick={() => location.reload()}
            >
              {t("setting.import.csv.step4.button")}
            </button>
          </div>
        </div>
      </section>
    </>
  )

  const renderBody = () => {
    switch (step) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      default:
        return (<></>)
    }
  }

  return (
    <section className="setting_page csv_import">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t('setting.import.csv.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.import.csv.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.import.csv.title')}
          <VideoGuide
            videoId="w7wN_EFQyyM"
          />
        </h1>
      </section>
      <section className="page_contents content-area">
        {renderBody()}
      </section>
    </section>
  )
}

export default Index
